import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { UpdatePromotionState } from "./update-promotion.state";
import { PromotionService } from "src/app/services/promotion.service";
import { PromotionModel } from "src/app/models/promotion-model";
import { UpdatePromotionModel } from "src/app/models/update-promotion-model";

const initialState: UpdatePromotionState = {
    loading: false,
    loaded: false,
    promotion: null,
    success: false,
    errorMessage: null,
};

@Injectable({
    providedIn: 'root'
})
export class UpdatePromotionComponentStore extends ComponentStore<UpdatePromotionState> {

    constructor(private readonly promotionService: PromotionService) {
        super(initialState);
    }

    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);
    readonly promotion$ : Observable<any> = this.select(state => state.promotion );

    readonly viewModel$ = this.select(
        this.promotion$,
        this.loaded$,
        this.loading$,
        this.success$,
        this.errorMessage$,
        (promotion, loaded, loading, success, errorMessage) => ({
            promotion, loaded, loading, success, errorMessage
        })
    );

    readonly setInitial = this.updater(( _ : UpdatePromotionState) => {
        return {
            ...initialState
        };
    });

    readonly setLoading = this.updater((state: UpdatePromotionState) => {
        return {
            ...state,
            loading: true,
            loaded: false
        };
    });

    readonly setLoaded = this.updater((state: UpdatePromotionState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });

    readonly updateDetailState = this.updater((state: UpdatePromotionState, value: { promotion : PromotionModel }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            promotion: value.promotion
        };
    });

    readonly setError = this.updater((state: UpdatePromotionState, value: {
        errorMessage: string
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly updatePromotion = this.effect((detailParams$: Observable<{ companyId: string, promotionId: number, updatePromotion : UpdatePromotionModel }>) => {
        this.setLoading();
        return detailParams$.pipe(
            switchMap((params => this.promotionService.updatePromotion(params.companyId, params.promotionId, params.updatePromotion).pipe(
                map((promotion) => {
                    this.updateDetailState({promotion: promotion});
                    this.setLoaded();
                    this.setInitial();
                }
                ),
                catchError(async (error) => {
                    this.setError({ errorMessage: error });
                    this.setLoaded();
                    this.setInitial();
                })
            ))
            )
        );
    });
}